import { useCallback, useState } from 'react';

/**
 * Boolean toggle whose value persists in localStorage under the
 * given key. Survives navigation + page reload. Falls back gracefully
 * when localStorage is unavailable (SSR, private windows with quota
 * errors, etc.) — the state still works in-memory; persistence is
 * best-effort.
 *
 * Returns [value, toggle]. Use a stable namespaced key like
 * "kg.jobDetail.timelineHidden" so different toggles don't collide.
 *
 * Intentionally tiny — no setter that takes a value, no SSR-aware
 * sync, no useEffect storage listener. Callers that need any of
 * those should reach for a fuller solution.
 */
export function useStickyToggle(
  key: string,
  defaultValue = false,
): [boolean, () => void] {
  const [value, setValue] = useState<boolean>(() => {
    if (typeof window === 'undefined') return defaultValue;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw === null) return defaultValue;
      return raw === '1';
    } catch {
      return defaultValue;
    }
  });
  const toggle = useCallback(() => {
    setValue((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(key, next ? '1' : '0');
      } catch {
        /* localStorage disabled or quota exceeded — keep state in memory */
      }
      return next;
    });
  }, [key]);
  return [value, toggle];
}
